/*eslint-disable*/
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addNewCoupon,
  listAllCoupons,
  resetAllCouponsData,
  updateCoupon,
  updateCouponStatus,
} from "../../store/actions";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import { tableStyle } from "../../components";
import { tokens } from "../../theme";
import { Formik } from "formik";
import { Add, Edit } from "@mui/icons-material";
import Header from "../../components/Header";
import * as yup from "yup";
import moment from "moment";

const Coupons = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setSelectedCoupon(null);
    setEditMode(false);
    setOpen(false);
  };

  const {
    isLoading,
    couponsList,
    showViewMore,
    hotelProfileId,
    pageNumber,
    totalCount,
  } = useSelector(({ loading, admin, registration }) => {
    const coupons = admin?.coupons || {};
    return {
      isLoading: loading,
      couponsList: coupons?.list || [],
      showViewMore: coupons?.showViewMore ?? false,
      hotelProfileId: registration?.hotelProfile?._id,
      pageNumber: coupons?.pageNumber ?? 1,
      totalCount: coupons?.totalCount ?? 0,
    };
  });

  useEffect(() => {
    document.title = "Coupons";
    // console.log("Coupons page mounted", couponsList);
    dispatch(resetAllCouponsData()); // Reset state on mount
    fetchMoreData(); // Fetch initial data
  }, [dispatch]);

  const fetchMoreData = () => {
    dispatch(listAllCoupons({ hotel_id: hotelProfileId })); 
  };

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <Stack width="100%" alignItems="center">
          {showViewMore && couponsList?.length < totalCount ? (
            <Button
              fullWidth
              onClick={fetchMoreData}
              disabled={isLoading || !showViewMore}
            >
              <b>{isLoading ? "Loading..." : "View More"}</b>
            </Button>
          ) : (
            <Typography variant="h6">
              {couponsList?.length === 0
                ? "No coupons found"
                : "All data listed"}
            </Typography>
          )}
        </Stack>
      </GridFooterContainer>
    );
  };
  const columns = [
    {
      field: "_id",
      headerName: "Coupon ID",
      width: 150,
      hide: true,
    },
    {
      field: "coupon_name",
      headerName: "Coupon Name",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "coupon_code",
      headerName: "Coupon Code",
      width: 180,
    },
    {
      field: "discount_value",
      headerName: "Discount Value",
      width: 150,
      valueGetter: (params) => `${params?.row?.discount_value || 0}%`,
    },
    {
      field: "redeem_count",
      headerName: "Redeem Count",
      width: 160,
    },
    {
      field: "active",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const currentDate = new Date();
        const checkOutDate = new Date(params.row.check_out_date * 1000); 
    
        const isExpired = currentDate > checkOutDate;
        const isActive = params.row.active && !isExpired;
    
        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "error"}
            size="small"
            style={{
              backgroundColor: isActive ? "green" : "red",
              color: "white",
            }}
          />
        );
      },
    },
    
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => handleEdit(params.row)} color="primary">
          <Edit />
        </IconButton>
      ),
    },
  ];

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: smScreen ? 400 : "90%",
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: 24,
    p: 2,
  };

  const handleFormSubmit = useCallback(
    async (values) => {
      const updatedValues = {
        ...values,
        hotel_id: hotelProfileId,
      };
      if (editMode) {
        await dispatch(updateCoupon(values._id, updatedValues.check_out_date));
      } else {
      await dispatch(addNewCoupon(updatedValues));
      }
      handleClose();
      fetchMoreData(); // Refresh list after adding a coupon
    },
    [dispatch, hotelProfileId, editMode]
  );

  const initialValues = {
    coupon_name: "",
    discount_value: "",
    check_out_date: "",
    check_in_date: "",
  };

  const checkoutSchema = yup.object().shape({
    coupon_name: yup.string().required("Required"),
    discount_value: yup
      .number()
      .required("Required")
      .min(1, "Discount must be between 1 and 99")
      .max(99, "Discount must be between 1 and 99"),
    check_in_date: yup
      .date()
      .required("Required"), 
    check_out_date: yup
      .date()
      .required("Required")
      .min(
        yup.ref("check_in_date"),
        "End date cannot be earlier than the start date"
      ),
  });

  const handleEdit = (coupon) => {
    const updatedCoupon = {
      ...coupon,
      check_in_date: moment.unix(coupon.check_in_date).format('YYYY-MM-DD'),
      check_out_date: moment.unix(coupon.check_out_date).format('YYYY-MM-DD'),
    };

    setEditMode(true);
    setSelectedCoupon(updatedCoupon);
    setOpen(true);
  };

  return (
    <Box m="20px" mb={15}>
      <Box
        display={smScreen ? "flex" : "block"}
        flexDirection={smScreen ? "row" : "column"}
        justifyContent={smScreen ? "space-between" : "start"}
        alignItems={smScreen ? "center" : "start"}
        m="20px 0"
      >
        <Header title="Coupons" subtitle="Welcome to Coupons" />
        <Box>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
            onClick={handleOpen}
          >
            <Add sx={{ mr: "10px" }} />
            Add New Coupon
          </Button>
        </Box>
      </Box>
      <Box m="8px 0 0 0" height="80vh" sx={tableStyle(colors)}>
        <DataGrid
          rows={couponsList || []}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row?._id}
          loading={isLoading}
        />
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Header
            title={editMode ? "EDIT COUPON" : "ADD NEW COUPON"}
            titleVariant="h4"
          />
          <Formik
            onSubmit={handleFormSubmit}
            initialValues={selectedCoupon || initialValues}
            validationSchema={checkoutSchema}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Coupon Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.coupon_name}
                    name="coupon_name"
                    error={!!touched.coupon_name && !!errors.coupon_name}
                    helperText={touched.coupon_name && errors.coupon_name}
                    disabled={editMode} // Read-only in edit mode
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Discount Value"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.discount_value}
                    name="discount_value"
                    error={!!touched.discount_value && !!errors.discount_value}
                    helperText={touched.discount_value && errors.discount_value}
                    disabled={editMode} // Read-only in edit mode
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Check In Date"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.check_in_date}
                    name="check_in_date"
                    InputLabelProps={{ shrink: true }}
                    error={!!touched.check_in_date && !!errors.check_in_date}
                    helperText={touched.check_in_date && errors.check_in_date}
                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                    disabled={editMode} // Read-only in edit mode
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Check Out Date"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.check_out_date}
                    name="check_out_date"
                    InputLabelProps={{ shrink: true }}
                    error={!!touched.check_out_date && !!errors.check_out_date}
                    helperText={touched.check_out_date && errors.check_out_date}
                    inputProps={{ 
                      min: editMode ? values.check_out_date : values.check_in_date 
                    }}
                  />
                  <Button
                    fullWidth
                    size="large"
                    type="submit"
                    color="secondary"
                    variant="contained"
                    sx={{ fontSize: "15px" }}
                  >
                    {isLoading ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      <b>{editMode ? "Update" : "Submit"}</b>
                    )}
                  </Button>
                </Stack>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
    </Box>
  );
};

export default Coupons;
